import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useCallback, useEffect, useMemo } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Unstable_Grid2';
import { DatePicker } from '@mui/x-date-pickers';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, FormControlLabel, Link, Switch } from '@mui/material';

import { RouterLink } from 'src/routes/components';

import axiosInstance from 'src/utils/axios';
import { hasPermission } from 'src/utils/has-permissions';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { createEmpployee, TCreateEmployee, updateEmployee } from 'src/redux/slices/employees';

import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFTextField, RHFSelect, RHFUpload } from 'src/components/hook-form';

import { BANK_OPTIONS, GENDER_OPTIONS, Gender, IEmployeeItem } from 'src/types/employee';

import { paths } from '../../routes/paths';
import { useAppDispatcher } from '../../redux/store';

export type ConditionalSchema<T> = T extends string
  ? Yup.StringSchema
  : T extends number
  ? Yup.NumberSchema
  : T extends boolean
  ? Yup.BooleanSchema
  : T extends Record<any, any>
  ? Yup.AnyObjectSchema
  : T extends Array<any>
  ? Yup.ArraySchema<any, any>
  : Yup.AnySchema;

export type Shape<Fields> = {
  [Key in keyof Fields]: ConditionalSchema<Fields[Key]>;
};

type Props = {
  currentEmployee?: IEmployeeItem;
  employeeNumber?: string;
};

// Raw upload cap before server-side compression - matches raw-server's
// NIC_IMAGE_MAX_UPLOAD_BYTES multer limit, so phone photos (typically 8-12MB)
// aren't rejected client-side before the server gets a chance to compress them.
const NIC_IMAGE_MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

// These must match messages.error.nicFrontImageTooLarge / nicBackImageTooLarge
// in raw-server's src/common/config/messages.ts, since the server is what
// enforces the actual post-compression size cap.
const NIC_FRONT_IMAGE_TOO_LARGE_MESSAGE =
  'NIC front image is still too large after compression (max 4MB). Please use a smaller or clearer photo.';
const NIC_BACK_IMAGE_TOO_LARGE_MESSAGE =
  'NIC back image is still too large after compression (max 4MB). Please use a smaller or clearer photo.';

export default function EmployeeNewEditForm({ currentEmployee, employeeNumber }: Props) {
  const dispatch = useAppDispatcher();

  const { user: authUser } = useAuthContext();

  const canUploadNic = hasPermission(
    authUser,
    'any',
    AuthPermissions.UPLOAD_EMPLOYEE,
    AuthPermissions.ADMIN_PERMISSION,
    AuthPermissions.SUPER_ADMIN_PERMISSION
  );

  const isAdmin = hasPermission(
    authUser,
    'any',
    AuthPermissions.ADMIN_PERMISSION,
    AuthPermissions.SUPER_ADMIN_PERMISSION
  );

  const showEpf = !!currentEmployee || isAdmin;

  const { enqueueSnackbar } = useSnackbar();

  const navigate = useNavigate();

  const NewEmployeeSchema = Yup.object<Shape<TCreateEmployee>>().shape({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    employeeNumber: Yup.string().required('Employee number is required'),
    baseSalary: Yup.number().required('Base salary is required'),
    epf: Yup.boolean().default(false),
    nicNumber: Yup.string()
      .required('NIC number is required')
      .test(
        'nic-format',
        'Enter a valid NIC number (old: 9 digits + V/X, new: 12 digits)',
        (value) => !!value && (/^\d{9}[VXvx]$/.test(value) || /^\d{12}$/.test(value))
      ),
    birthDate: Yup.date().required('Birthdate is required'),
    gender: Yup.mixed<Gender>().oneOf(Object.values(Gender)).required('Gender is required'),
    joinedAt: Yup.date().required('Joined date is required'),
    phoneNumber: Yup.string().required('Phone number is required'),
    contactPersonName: Yup.string().required('Contact person name is required'),
    contactPersonPhoneNumber: Yup.string().required('Contact person phone number is required'),
    address: Yup.string().required('Address is required'),
    bankDetails: Yup.object()
      .shape({
        accountName: Yup.string().nullable(),
        accountNumber: Yup.string().nullable(),
        bank: Yup.string().nullable(),
      })
      .nullable(),
    nicFrontImage: currentEmployee
      ? Yup.mixed<File | string>().nullable()
      : Yup.mixed<File | string>().required('NIC front image is required'),
    nicBackImage: currentEmployee
      ? Yup.mixed<File | string>().nullable()
      : Yup.mixed<File | string>().required('NIC back image is required'),
  });

  const defaultValues = useMemo(
    () => ({
      firstName: currentEmployee?.firstName || '',
      lastName: currentEmployee?.lastName || '',
      employeeNumber: currentEmployee?.employeeNumber || employeeNumber || '',
      baseSalary: currentEmployee?.baseSalary || 800,
      epf: currentEmployee?.epf || false,
      nicNumber: currentEmployee?.nicNumber || '',
      birthDate: currentEmployee?.birthDate
        ? new Date(currentEmployee.birthDate)
        : new Date(2000, 0, 1),
      gender: (currentEmployee?.gender ?? '') as Gender,
      joinedAt: currentEmployee?.joinedAt ? new Date(currentEmployee.joinedAt) : new Date(),
      phoneNumber: currentEmployee?.phoneNumber || '',
      contactPersonName: currentEmployee?.contactPersonName || '',
      contactPersonPhoneNumber: currentEmployee?.contactPersonPhoneNumber || '',
      address: currentEmployee?.address || '',
      bankDetails: currentEmployee?.bankDetails || { accountName: '', accountNumber: '', bank: '' },
      nicFrontImage: undefined as File | string | null | undefined,
      nicBackImage: undefined as File | string | null | undefined,
    }),
    [currentEmployee, employeeNumber]
  );

  const methods = useForm({
    resolver: yupResolver(NewEmployeeSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting },
    trigger,
    setValue,
    setError,
  } = methods;

  const handleSubmitError = useCallback(
    (e: unknown) => {
      const message = typeof e === 'string' ? e : (e as { message?: string })?.message;

      if (message === NIC_FRONT_IMAGE_TOO_LARGE_MESSAGE) {
        setError('nicFrontImage', { message });
        return;
      }

      if (message === NIC_BACK_IMAGE_TOO_LARGE_MESSAGE) {
        setError('nicBackImage', { message });
        return;
      }

      enqueueSnackbar(message, { variant: 'error' });
    },
    [enqueueSnackbar, setError]
  );

  const onSubmit = handleSubmit(async (data) => {
    if (currentEmployee) {
      // update
      try {
        await dispatch(
          updateEmployee({
            employeeId: currentEmployee._id,
            employee: {
              ...data,
              firstName: data.firstName.trimStart().replace(/^\w/, (c) => c.toUpperCase()),
            } as TCreateEmployee,
          })
        ).unwrap();

        enqueueSnackbar('Changes were made successfully');
        navigate(paths.dashboard.employees.root);
        reset();
      } catch (e) {
        handleSubmitError(e);
      }
    } else {
      try {
        await dispatch(
          createEmpployee({
            employee: {
              ...data,
              firstName: data.firstName.trimStart().replace(/^\w/, (c) => c.toUpperCase()),
            } as TCreateEmployee,
          })
        ).unwrap();

        enqueueSnackbar('Employee created successfully');

        navigate(paths.dashboard.employees.root);
        reset();
      } catch (e) {
        handleSubmitError(e);
      }
    }
  });

  useEffect(() => {
    if (employeeNumber) {
      setValue('employeeNumber', employeeNumber);
    }
  }, [employeeNumber, setValue]);

  useEffect(() => {
    if (!currentEmployee?._id) return undefined;

    const objectUrls: string[] = [];

    const loadImage = async (
      side: 'front' | 'back',
      hasImage: boolean,
      fieldName: 'nicFrontImage' | 'nicBackImage'
    ) => {
      if (!hasImage) return;
      try {
        const response = await axiosInstance.get(
          `employee/${currentEmployee._id}/nic-image/${side}`,
          { responseType: 'blob' }
        );
        const url = URL.createObjectURL(response.data);
        objectUrls.push(url);
        setValue(fieldName, url);
      } catch (e) {
        // no image uploaded for this employee yet (legacy record) - leave the field empty
      }
    };

    loadImage('front', !!currentEmployee.nicFrontImage?.key, 'nicFrontImage');
    loadImage('back', !!currentEmployee.nicBackImage?.key, 'nicBackImage');

    return () => objectUrls.forEach((url) => URL.revokeObjectURL(url));
  }, [currentEmployee, setValue]);

  const handleDropFront = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file) {
        const newFile = Object.assign(file, { preview: URL.createObjectURL(file) });
        setValue('nicFrontImage', newFile, { shouldValidate: true });
      }
    },
    [setValue]
  );

  const handleDropBack = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file) {
        const newFile = Object.assign(file, { preview: URL.createObjectURL(file) });
        setValue('nicBackImage', newFile, { shouldValidate: true });
      }
    },
    [setValue]
  );

  const handleRemoveFront = useCallback(() => {
    setValue('nicFrontImage', null, { shouldValidate: true });
  }, [setValue]);

  const handleRemoveBack = useCallback(() => {
    setValue('nicBackImage', null, { shouldValidate: true });
  }, [setValue]);

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid xs={12}>
          <Card sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid
                mb={1}
                display="flex"
                mt={2}
                alignItems="center"
                justifyContent="space-between"
                width={1}
              >
                <Typography variant="subtitle2">Personal Details</Typography>
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField required name="firstName" label="First Name" />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField required name="lastName" label="Last Name" />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField
                  required
                  name="employeeNumber"
                  label="Employee Number"
                  disabled={!!currentEmployee || !isAdmin}
                />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField required name="nicNumber" label="NIC Number" />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFSelect required name="gender" label="Gender">
                  {GENDER_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </Grid>

              <Grid xs={12} md={6}>
                <Controller
                  name="birthDate"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DatePicker
                      label="Birthdate"
                      value={field.value}
                      onChange={(newValue) => field.onChange(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!error,
                          helperText: error?.message,
                          required: true,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid xs={12} md={6}>
                <Controller
                  name="joinedAt"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DatePicker
                      label="Joined At"
                      value={field.value}
                      onChange={(newValue) => field.onChange(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!error,
                          helperText: error?.message,
                          required: true,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField required name="phoneNumber" label="Phone Number" />
              </Grid>

              <Grid xs={12}>
                <RHFTextField required multiline rows={2} name="address" label="Address" />
              </Grid>

              <Grid mb={1} xs={12} width="full">
                <Typography mt={2} variant="subtitle2">
                  Contact Person
                </Typography>
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField required name="contactPersonName" label="Contact Person Name" />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField
                  required
                  name="contactPersonPhoneNumber"
                  label="Contact Person Phone Number"
                />
              </Grid>

              <Grid mb={1} xs={12} width="full">
                <Typography mt={2} variant="subtitle2">
                  Salary &amp; EPF
                </Typography>
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField type="number" required name="baseSalary" label="Base Salary" />
              </Grid>

              {showEpf && (
                <Grid xs={12} md={6}>
                  <FormControlLabel
                    labelPlacement="start"
                    control={
                      <Controller
                        name="epf"
                        control={control}
                        render={({ field }) => <Switch {...field} checked={field.value} />}
                      />
                    }
                    label={
                      <>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          Is EPF Deducting?
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Turn on the switch if the employee is eligible for EPF deduction.
                        </Typography>
                      </>
                    }
                    sx={{ mx: 0, mb: 3, width: 1, justifyContent: 'space-between' }}
                  />
                </Grid>
              )}

              <Grid mb={1} xs={12} width="full">
                <Typography mt={2} variant="subtitle2">
                  Bank Details (Optional)
                </Typography>
              </Grid>

              <Grid xs={12} md={4}>
                <RHFTextField name="bankDetails.accountName" label="Account Name" />
              </Grid>

              <Grid xs={12} md={4}>
                <RHFTextField name="bankDetails.accountNumber" label="Account Number" />
              </Grid>

              <Grid xs={12} md={4}>
                <RHFSelect name="bankDetails.bank" label="Bank">
                  <MenuItem value="">None</MenuItem>
                  {BANK_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </Grid>

              {canUploadNic && (
                <>
                  <Grid mb={1} xs={12} width="full">
                    <Typography mt={2} variant="subtitle2">
                      NIC Images
                    </Typography>
                  </Grid>

                  <Grid xs={12} md={6}>
                    <RHFUpload
                      name="nicFrontImage"
                      maxSize={NIC_IMAGE_MAX_UPLOAD_BYTES}
                      onDrop={handleDropFront}
                      onDelete={handleRemoveFront}
                      helperText="NIC front image (JPEG/PNG, up to 15MB - optimized automatically)"
                    />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <RHFUpload
                      name="nicBackImage"
                      maxSize={NIC_IMAGE_MAX_UPLOAD_BYTES}
                      onDrop={handleDropBack}
                      onDelete={handleRemoveBack}
                      helperText="NIC back image (JPEG/PNG, up to 15MB - optimized automatically)"
                    />
                  </Grid>
                </>
              )}
            </Grid>

            <Stack alignItems="flex-end" sx={{ mt: 3 }}>
              <Stack direction="row" gap={2}>
                <Link component={RouterLink} href={paths.dashboard.employees.root}>
                  <Button variant="outlined" color="inherit">
                    Cancel
                  </Button>
                </Link>

                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  onClick={() => trigger()}
                >
                  {!currentEmployee ? 'Create Employee' : 'Save Changes'}
                </LoadingButton>
              </Stack>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
